import { ref } from 'vue'
import api from '@/api'
import { Amplify } from 'aws-amplify'
import {
  getCompanyModuleAccess,
  hasCompanyModuleAccess,
  resolveCompanyModuleAccessKey
} from '@/utils/companyAccess'
import {
  signIn,
  confirmSignIn,
  signInWithRedirect,
  fetchAuthSession
} from 'aws-amplify/auth'
import { identifyPostHogUser } from '@/utils/posthog'

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
      // identityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID
      loginWith: {
        oauth: {
          domain: import.meta.env.VITE_COGNITO_DOMAIN,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [import.meta.env.VITE_COGNITO_REDIRECT_URI],
          redirectSignOut: [import.meta.env.VITE_COGNITO_REDIRECT_URI],
          responseType: 'code'
        }
      }
    }
  }
})

const baseRoutes = [
  'Home',
  'UserProfile',
  'Diligence',
  'DiligenceDocs',
  'DiligenceKanban',
  'Entity',
  'Documents',
  'DocumentAnalysis',
  'PersonalDocumentAnalysis',
  'DiligenceSight',
  'DiligenceReport',
  'DiligenceCustomReport',
  'DiligenceStandardReport',
  'EntityDocs',
  'RegistrationAnalysis',
  'LawSuits',
  'Dossier',
  'GenerateContract',
  'Transfer',
  'DocumentDetails',
  'DocumentProcessor',
  'DocumentList',
  'DiligenceLawSuits',
  'EntitiesQSA',
  'Workspace',
  'SpcSearch',
  'BatchEntities',
  'ReviewBatchEntities',
  'ExternalUploadPortal',
  'ExternalUploadSettings',
  'SmartFillRunProcessing',
  'SmartFillReview',
  'TransferUnits',
  'TransferUnitComparison'
]

const admClientRoutes = [
  'Settings',
  'Tracks',
  'EditTrack',
  'ReviewTrack',
  'NewTrack',
  'Reports',
  'FinancialStatementCompany',
  'UsersManagement',
  'Template',
  'TemplateAnalysis',
  'ApiKeys'
]

const quillRoutes = [
  'Robots',
  'Commercial',
  'CommercialRobotDebug',
  'BillingDashboard',
  'LegacyCompaniesMetrics',
  'TrackCalculator',
  'Coverage',
  'SmartFillTemplateSetup',
  'SmartFillTemplates',
  'ApiMonitoring',
  'ONR',
  'ONRPlayground'
]

const admRoutes = [
  'Users',
  'Companies',
  'DocumentTypes',
  'Terms',
  'FinancialStatement',
  'TracksSimplified',
  'EditTrackSimplified',
  'NewTrackSimplified',
  'TransactionScoreSettings'
]

const quillAdminRoutes = ['ONR', 'ONRPlayground']

const rolePermissions = {
  QUILL_ADMIN: [
    ...baseRoutes,
    ...admClientRoutes,
    ...quillRoutes,
    ...admRoutes,
    ...quillAdminRoutes
  ],
  QUILL_DEFAULT: [
    ...baseRoutes,
    ...admClientRoutes,
    ...quillRoutes.filter((route) => route !== 'Robots'),
    ...admRoutes
  ],
  ADMIN: [...baseRoutes, ...admClientRoutes],
  DEFAULT: [...baseRoutes]
}

interface Company {
  _id: string
  name: string
  cnpj: string
  processOnDemand?: boolean
  accessConfigurations?: Array<{
    name?: string
    tag?: string
    configuration?: Record<string, boolean>
  }>
}

export interface IUser {
  _id: string
  name: string
  email: string
  cpf: string
  avatar: string
  role: string
  company: Company
  createdAt: string
  AccessToken: string
  RefreshToken: string
}

export interface VerifyDomainResponse {
  identityProvider: string | null
}

export interface AmplifySignInResult {
  nextStep: {
    signInStep: string
  }
  isSignedIn: boolean
}

const getNewToken = async (refreshToken: string) => {
  return api.get('/users/auth', {
    headers: {
      RefreshToken: refreshToken
    }
  })
}

const identifyAnalyticsUser = (userData: IUser) => {
  const company =
    userData.company && typeof userData.company !== 'string'
      ? userData.company
      : null

  identifyPostHogUser({
    userId: userData._id,
    email: userData.email,
    name: userData.name,
    role: userData.role,
    companyId: company?._id || '',
    companyName: company?.name || '',
    companyCnpj: company?.cnpj || ''
  })
}

export function useAuth() {
  const user = ref<IUser>({
    _id: '',
    name: '',
    email: '',
    cpf: '',
    avatar: '',
    company: { _id: '', name: '', cnpj: '' },
    role: 'DEFAULT',
    createdAt: '',
    AccessToken: '',
    RefreshToken: ''
  })

  const authKey = localStorage.getItem('auth')
  if (authKey) {
    user.value = JSON.parse(authKey)
    identifyAnalyticsUser(user.value)
  }

  const hasRolePermissionForResource = (resource: string) => {
    //@ts-ignore
    return Boolean(rolePermissions[user.value.role]?.includes(resource))
  }

  const getCompanyModulePermission = (resource: string) => {
    const moduleAccessKey = resolveCompanyModuleAccessKey(resource)
    if (!moduleAccessKey) return true

    const company =
      user.value.company && typeof user.value.company !== 'string'
        ? user.value.company
        : null
    if (!company) return true

    const companyModuleAccess = getCompanyModuleAccess(
      company.accessConfigurations
    )
    return hasCompanyModuleAccess(companyModuleAccess, moduleAccessKey)
  }

  const isCompanyModuleRestricted = (resource: string) => {
    if (!hasRolePermissionForResource(resource)) return false
    return !getCompanyModulePermission(resource)
  }

  const isQuill = () => {
    return ['QUILL_ADMIN', 'QUILL_DEFAULT'].includes(user.value.role)
  }

  const canDisplayResource = (resource: string) => {
    return hasRolePermissionForResource(resource)
  }

  const hasPermission = (resource: string) => {
    const hasRolePermission = hasRolePermissionForResource(resource)
    if (!hasRolePermission) return false
    return !isCompanyModuleRestricted(resource)
  }

  const isJwtExpired = async (): Promise<boolean> => {
    const token = user.value?.AccessToken
    if (!token) return true

    try {
      const [, base64Url] = token.split('.')
      if (!base64Url) throw new Error('Invalid JWT token format')

      const jsonPayload = JSON.parse(atob(base64Url))
      const exp = jsonPayload.exp

      if (!exp) throw new Error("No 'exp' field in token")

      if (Math.floor(Date.now() / 1000) < exp) return false

      const refreshToken = user.value?.RefreshToken
      if (!refreshToken) return true

      const response = await getNewToken(refreshToken)
      if (response.status !== 200) throw new Error('Failed to refresh token')

      if (!authKey) return true
      const newSession = {
        ...JSON.parse(authKey),
        AccessToken: response.data.AccessToken
      }
      localStorage.setItem('auth', JSON.stringify(newSession))
      user.value = newSession

      return false
    } catch (error) {
      console.error('Error decoding token:', error)
      return true
    }
  }

  /**
   * Verifica se o domínio do e-mail possui um identity provider configurado no Cognito.
   */
  const verifyDomain = async (email: string): Promise<VerifyDomainResponse> => {
    const domain = email.split('@')[1]
    const response = await api.get<VerifyDomainResponse>(
      '/users/verify-domain',
      {
        params: { domain }
      }
    )
    return response.data
  }

  /**
   * Inicia o login com usuário e senha via Amplify.
   * Retorna o resultado que pode conter um nextStep (challenge) ou isSignedIn=true.
   */
  const amplifySignIn = async (
    email: string,
    password: string
  ): Promise<AmplifySignInResult> => {
    const result = await signIn({ username: email, password })
    return result as AmplifySignInResult
  }

  /**
   * Responde a um challenge do Amplify (MFA ou outro).
   */
  const amplifyConfirmSignIn = async (
    code: string
  ): Promise<AmplifySignInResult> => {
    const result = await confirmSignIn({ challengeResponse: code })
    return result as AmplifySignInResult
  }

  /**
   * Busca os tokens da sessão atual do Amplify e os dados do usuário via API.
   * Salva no localStorage e retorna o usuário.
   */
  const fetchAndStoreUser = async (userId: string): Promise<IUser> => {
    const session = await fetchAuthSession()
    const accessToken = session.tokens?.accessToken?.toString() ?? ''
    const idToken = session.tokens?.idToken?.toString() ?? ''

    // Guarda token temporariamente para o interceptor da API incluir o header Authorization
    localStorage.setItem('auth', JSON.stringify({ AccessToken: accessToken }))

    const userResponse = await api.get<IUser>(`/users/login/${userId}`)

    const userData: IUser = {
      ...userResponse.data,
      AccessToken: accessToken,
      RefreshToken: idToken
    }

    localStorage.setItem('auth', JSON.stringify(userData))
    user.value = userData
    identifyAnalyticsUser(userData)

    return userData
  }

  /**
   * Retorna o ID (sub) do usuário autenticado a partir do idToken da sessão.
   * Não requer scopes adicionais — funciona tanto no login por senha quanto no SSO.
   */
  const getAmplifyUserId = async (): Promise<string | null> => {
    const session = await fetchAuthSession()
    const payload = session.tokens?.idToken?.payload
    return (payload?.sub as string) ?? null
  }

  /**
   * Login via SSO — redireciona para o identity provider externo do Cognito.
   */
  const ssoLogin = async (provider: string) => {
    await signInWithRedirect({
      provider: { custom: provider } as any
    })
  }

  return {
    user,
    isQuill,
    canDisplayResource,
    hasRolePermissionForResource,
    hasPermission,
    isCompanyModuleRestricted,
    isJwtExpired,
    verifyDomain,
    amplifySignIn,
    amplifyConfirmSignIn,
    fetchAndStoreUser,
    getAmplifyUserId,
    ssoLogin
  }
}
